import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { errorDetail } from "@/lib/utils";
import { PLATFORM, BASE, TOKEN_KEY, USER_KEY, hashPlatformToken } from "@/lib/platform";

const AuthContext = createContext(null);
let exchangeStarted = false; // the token is single-use (StrictMode runs effects twice)

// Store a login response (password login or platform exchange) the same way.
function saveSession(data) {
  localStorage.setItem(TOKEN_KEY, data.token);
  const u = { email: data.email, name: data.name, role: data.role };
  localStorage.setItem(USER_KEY, JSON.stringify(u));
  return u;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem(USER_KEY);
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(false);
  // Opened from the platform launcher (#platform_token=...): sign in with it
  // before anything decides to show a login page.
  const [exchanging, setExchanging] = useState(() => !!hashPlatformToken());
  const [platformError, setPlatformError] = useState("");

  useEffect(() => {
    const token = hashPlatformToken();
    if (!token || exchangeStarted) return;
    exchangeStarted = true;
    (async () => {
      let path = window.location.pathname + window.location.search;
      try {
        const { data } = await api.post("/auth/platform-exchange", { token });
        setUser(saveSession(data));
        if (path.replace(/\/+$/, "") === BASE) path = `${BASE}/admin`; // the launcher opens the app's root
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setPlatformError(errorDetail(e) || "Sign-in from the platform failed");
        path = `${BASE}/admin/login`;
      }
      window.history.replaceState(null, "", path);
      setExchanging(false);
    })();
  }, []);

  // Self-heal: if we have a token but role is missing (legacy session), fetch /auth/me
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || exchanging) return;
    if (user && user.role) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const next = {
          email: data.email,
          name: user?.name || data.email,
          role: data.role || "owner",
        };
        localStorage.setItem(USER_KEY, JSON.stringify(next));
        setUser(next);
      } catch {
        // token invalid, leave as-is
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(saveSession(data));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: errorDetail(e) || "Login failed" };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    if (PLATFORM) {
      window.location.assign("/"); // back to the platform launcher
      return;
    }
    setUser(null);
  };

  if (exchanging) return null; // a moment; the router mounts on the final URL

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, platformError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
